import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Upload, CheckCircle } from 'lucide-react';
import { sendAbstractConfirmationEmail } from '../lib/email';

interface Author {
  name: string;
  institution: string;
}

interface FormData {
  title: string;
  corresponding_author: string;
  institution: string;
  email: string;
  phone: string;
  abstract_type: string;
  content: string;
  keywords: string;
  coauthors: Author[];
  document_url?: string;
  documentFileName?: string;
}

export function AbstractSubmissionPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccessful, setSubmissionSuccessful] = useState(false);
  const [submittedData, setSubmittedData] = useState<FormData | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    corresponding_author: '',
    institution: '',
    email: '',
    phone: '',
    abstract_type: '',
    content: '',
    keywords: '',
    coauthors: []
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [submissionSuccessful]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 25 * 1024 * 1024) { // 25MB limit
        toast.error('Datoteka je prevelika. Maksimalna veličina je 25MB.');
        return;
      }
      
      // Provjera formata datoteke
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (fileExt !== 'pdf' && fileExt !== 'pptx' && fileExt !== 'doc' && fileExt !== 'docx') {
        toast.error('Dozvoljeni su samo .pdf, .pptx, .doc i .docx formati.');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.abstract_type) {
      toast.error('Molimo odaberite vrstu prezentacije');
      setIsSubmitting(false);
      return;
    }

    try {
      // Priprema podataka za slanje u bazu
      const submissionData = {
        ...formData,
        authors: [], // Inicijaliziraj authors kao prazan niz
        coauthors: JSON.stringify(formData.coauthors),
        document_url: null // Inicijalno postaviti na null
      };

      // 1. Prvo pošalji podatke bez dokumenta
      const { data: insertedData, error: insertError } = await supabase
        .from('abstracts')
        .insert([submissionData])
        .select();

      if (insertError) {
        console.error('Error inserting data:', insertError);
        throw new Error(`Greška pri slanju podataka: ${insertError.message}`);
      }

      // 2. Ako je odabrana datoteka, pokušaj upload
      if (selectedFile && insertedData && insertedData.length > 0) {
        try {
          // Generiranje jedinstvenog imena datoteke
          const fileExt = selectedFile.name.split('.').pop()?.toLowerCase();
          const fileName = `abstract_document_${insertedData[0].id}_${Date.now()}.${fileExt}`;
          
          console.log(`Započinjem upload datoteke: ${fileName}`);
          
          // Određivanje MIME tipa
          let contentType = 'application/octet-stream';
          if (fileExt === 'pdf') contentType = 'application/pdf';
          else if (fileExt === 'pptx') contentType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
          else if (fileExt === 'doc') contentType = 'application/msword';
          else if (fileExt === 'docx') contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          
          // Upload datoteke
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('abstracts_documents')
            .upload(fileName, selectedFile, {
              contentType,
              cacheControl: '3600',
              upsert: true
            });

          if (uploadError) {
            console.error('Upload error:', uploadError);
            
            // Nastavi bez datoteke ako je došlo do greške pri uploadu
            toast.error('Došlo je do greške pri uploadu datoteke, ali vaši ostali podaci su uspješno spremljeni.');
          } else {
            // Upload je uspio, dohvati javni URL
            const { data: urlData } = supabase.storage
              .from('abstracts_documents')
              .getPublicUrl(fileName);
            
            if (urlData && urlData.publicUrl) {
              // Ažuriraj zapis s URL-om dokumenta
              const { error: updateError } = await supabase
                .from('abstracts')
                .update({ document_url: urlData.publicUrl })
                .eq('id', insertedData[0].id);
              
              if (updateError) {
                console.error('Error updating document URL:', updateError);
              }
            }
          }
        } catch (uploadError) {
          console.error('Exception during upload:', uploadError);
          // Nastavi s uspješnom prijavom čak i ako upload nije uspio
        }
      }

      sendAbstractConfirmationEmail({
        title: formData.title,
        correspondingAuthor: formData.corresponding_author,
        institution: formData.institution,
        email: formData.email,
        phone: formData.phone,
        abstractType: formData.abstract_type,
        content: formData.content,
        keywords: formData.keywords,
        coauthors: formData.coauthors,
        documentFileName: selectedFile?.name
      }).catch((err) => {
        console.error('Slanje email potvrde za sažetak nije uspjelo:', err);
      });

      toast.success('Sažetak je uspješno poslan!');

      setSubmittedData({
        ...formData,
        documentFileName: selectedFile?.name
      });
      setSubmissionSuccessful(true);
      setSelectedFile(null);
    } catch (error: any) {
      console.error('Error during form submission:', error);
      toast.error(`Došlo je do greške prilikom slanja sažetka: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCoauthorChange = (index: number, field: keyof Author, value: string) => {
    const updatedCoauthors = [...formData.coauthors];
    updatedCoauthors[index] = { ...updatedCoauthors[index], [field]: value };
    setFormData(prev => ({ ...prev, coauthors: updatedCoauthors }));
  };

  const addCoauthor = () => {
    setFormData(prev => ({
      ...prev,
      coauthors: [...prev.coauthors, { name: '', institution: '' }]
    }));
  };

  const removeCoauthor = (index: number) => {
    const updatedCoauthors = [...formData.coauthors];
    updatedCoauthors.splice(index, 1);
    setFormData(prev => ({ ...prev, coauthors: updatedCoauthors }));
  };

  const getAbstractTypeLabel = (value: string) => {
    switch (value) {
      case 'oral': return 'Usmeno izlaganje';
      case 'poster': return 'E-poster';
      default: return value;
    }
  };

  const SubmissionConfirmation = () => {
    if (!submittedData) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-xl overflow-hidden"
      >
        <div className="bg-gradient-to-r from-green-600 to-green-800 text-white p-8">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-center">
            Uspješna prijava sažetka!
          </h1>
          <p className="text-green-100 text-center mt-2">
            Hvala vam na prijavi za Kongres HDOMST 2026
          </p>
        </div>

        <div className="p-8">
          <div className="mb-8 bg-green-50 p-6 rounded-lg border border-green-200">
            <p className="text-gray-800">
              Vaš sažetak je uspješno zaprimljen i evidentiran u sustavu.
              Potvrda je poslana na email adresu <strong>{submittedData.email}</strong>.
              Stručni odbor će pregledati vaš sažetak i obavijestiti vas o statusu prijave.
            </p>
            <p className="text-gray-600 text-sm mt-3">
              Napomena: slanje sažetaka ne podrazumijeva ujedno i prijavu za sudjelovanje na kongresu.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-6">Pregled prijavljenih podataka</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Osnovni podaci</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Naslov rada</p>
                    <p className="text-gray-900 font-medium">{submittedData.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Vrsta prezentacije</p>
                    <p className="text-gray-900 font-medium">{getAbstractTypeLabel(submittedData.abstract_type)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Podaci o glavnom autoru</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Glavni autor</p>
                    <p className="text-gray-900 font-medium">{submittedData.corresponding_author}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Institucija</p>
                    <p className="text-gray-900 font-medium">{submittedData.institution}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-900 font-medium">{submittedData.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    <p className="text-gray-900 font-medium">{submittedData.phone}</p>
                  </div>
                </div>
              </div>
            </div>

            {submittedData.coauthors.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Koautori</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  {submittedData.coauthors.map((coauthor, index) => (
                    <div key={index} className="mb-3 pb-3 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0">
                      <p className="text-gray-900 font-medium">
                        {index + 1}. {coauthor.name}
                        {coauthor.institution ? ` (${coauthor.institution})` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Sažetak</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900 whitespace-pre-line">{submittedData.content}</p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Ključne riječi</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900">{submittedData.keywords}</p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Priloženi dokument</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900">
                  {submittedData.documentFileName || 'Nije priložen'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kongres"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-center"
            >
              Povratak na stranicu kongresa
            </Link>
            <button
              type="button"
              onClick={() => {
                setSubmissionSuccessful(false);
                setSubmittedData(null);
                setFormData({
                  title: '',
                  corresponding_author: '',
                  institution: '',
                  email: '',
                  phone: '',
                  abstract_type: '',
                  content: '',
                  keywords: '',
                  coauthors: []
                });
              }}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Podnesi novi sažetak
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto px-4"
      >
        {submissionSuccessful ? (
          <SubmissionConfirmation />
        ) : (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8">
            <h1 className="text-3xl font-bold text-center">Prijava sažetka</h1>
            <p className="text-blue-100 text-center mt-2">
              Molimo ispunite sve potrebne podatke za prijavu sažetka
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-8">
            {/* Title */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <h2 className="text-xl font-semibold text-gray-900">Naslov rada</h2>
              <div>
                <input
                  type="text"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Unesite naslov rada"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </motion.div>

            {/* Corresponding Author Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-6"
            >
              <h2 className="text-xl font-semibold text-gray-900">Podaci o glavnom autoru</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ime i prezime glavnog autora
                  </label>
                  <input
                    type="text"
                    name="corresponding_author"
                    required
                    value={formData.corresponding_author}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Institucija
                  </label>
                  <input
                    type="text"
                    name="institution"
                    required
                    value={formData.institution}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </motion.div>

            {/* Coauthors */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.35 }}
              className="space-y-6"
            >
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">Koautori</h2>
                <button
                  type="button"
                  onClick={addCoauthor}
                  className="px-3 py-1 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 flex items-center text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Dodaj koautora
                </button>
              </div>

              {formData.coauthors.length > 0 ? (
                <div className="space-y-4">
                  {formData.coauthors.map((coauthor, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-medium">Koautor {index + 1}</h3>
                        <button
                          type="button"
                          onClick={() => removeCoauthor(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Ime i prezime
                          </label>
                          <input
                            type="text"
                            required
                            value={coauthor.name}
                            onChange={(e) => handleCoauthorChange(index, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Institucija
                          </label>
                          <input
                            type="text"
                            value={coauthor.institution}
                            onChange={(e) => handleCoauthorChange(index, 'institution', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">Nema dodanih koautora. Kliknite na gumb za dodavanje koautora.</p>
              )}
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-6"
            >
              <h2 className="text-xl font-semibold text-gray-900">Kontakt podaci</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </motion.div>

            {/* Presentation Type */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="space-y-6"
            >
              <h2 className="text-xl font-semibold text-gray-900">Vrsta prezentacije</h2>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    name="abstract_type"
                    value="oral"
                    checked={formData.abstract_type === 'oral'}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="text-gray-700">Usmeno izlaganje</label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    name="abstract_type"
                    value="poster"
                    checked={formData.abstract_type === 'poster'}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="text-gray-700">E-poster</label>
                </div>
              </div>
            </motion.div>

            {/* Abstract */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="space-y-6"
            >
              <h2 className="text-xl font-semibold text-gray-900">Sažetak</h2>
              <div>
                <textarea
                  name="content"
                  required
                  value={formData.content}
                  onChange={handleInputChange}
                  rows={8}
                  placeholder="Unesite sažetak rada (maksimalno 300 riječi)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </motion.div>

            {/* Keywords */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.61 }}
              className="space-y-6 border-2 border-blue-200 p-4 rounded-lg bg-blue-50"
            >
              <h2 className="text-xl font-semibold text-gray-900">Ključne riječi</h2>
              <div>
                <input
                  type="text"
                  name="keywords"
                  required
                  value={formData.keywords}
                  onChange={handleInputChange}
                  placeholder="Unesite ključne riječi odvojene zarezom (npr. kardiologija, hipertenzija, terapija)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Unesite 3-5 ključnih riječi odvojenih zarezom</p>
              </div>
            </motion.div>

            {/* Upload dokument */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.65 }}
              className="space-y-6"
            >
              <h2 className="text-xl font-semibold text-gray-900">Upload dokumenta (opciono)</h2>
              <div className="flex flex-col items-center justify-center w-full">
                <label
                  htmlFor="file-upload"
                  className="w-full flex flex-col items-center justify-center px-4 py-6 border-2 border-blue-300 border-dashed rounded-lg cursor-pointer bg-blue-50 hover:bg-blue-100 transition-colors duration-200"
                >
                  <Upload className="w-12 h-12 text-blue-500 mb-3" />
                  <div className="flex flex-col items-center">
                    <p className="mb-2 text-sm text-blue-500">
                      <span className="font-semibold">Kliknite za upload</span> ili povucite i ispustite
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF, PPTX, DOC ili DOCX (max. 25MB)
                    </p>
                  </div>
                  {selectedFile && (
                    <div className="mt-4 text-sm text-blue-600">
                      Odabrana datoteka: {selectedFile.name}
                    </div>
                  )}
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="hidden"
                    accept=".pdf,.pptx,.doc,.docx"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">
                  Napomena: Možete odmah priložiti dokument (prezentaciju ili rad) ili ga naknadno učitati putem posebnog obrasca. 
                  Sažetak će biti recenziran, a vi ćete biti obaviješteni o prihvaćanju sažetka.
                </p>
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="pt-6"
            >
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 px-8 text-lg font-bold text-white bg-blue-600 rounded-full 
                  ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'} 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
                  transition-colors duration-200`}
              >
                {isSubmitting ? 'Slanje sažetka...' : 'Pošalji sažetak'}
              </button>
            </motion.div>
          </form>
        </div>
        )}
      </motion.div>
    </div>
  );
}